import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebase';
import BoardLikeButton from './BoardLikeButton';
import BoardAnchorPopup from './BoardAnchorPopup';

function formatDateCh(seconds) {
  const date = new Date(seconds * 1000);
  const days = ['日', '月', '火', '水', '木', '金', '土'];
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const day = days[date.getDay()];
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d}(${day}) ${h}:${min}`;
}

function processContent(content, replies, startIndex) {
  const anchorRegex = />>(\d+)/g;
  const parts = content.split(anchorRegex);

  return parts.map((part, index) => {
    const match = part.match(/^(\d+)$/);
    if (!match) return <span key={index}>{part}</span>;

    const replyNumber = parseInt(match[1], 10);
    const replyIndex = replyNumber - startIndex;
    const reply = replies[replyIndex];

    if (!reply) {
      return (
        <span key={index} style={{ color: 'var(--ch-anchor)', cursor: 'default' }}>
          &gt;&gt;{replyNumber}
        </span>
      );
    }

    return (
      <BoardAnchorPopup
        key={index}
        replyNumber={replyNumber}
        content={reply.content}
        authorName={reply.authorName}
        createdAt={reply.createdAt ? formatDateCh(reply.createdAt.seconds) : ''}
      >
        &gt;&gt;{replyNumber}
      </BoardAnchorPopup>
    );
  });
}

function BoardReplyList({ threadId, startIndex = 1 }) {
  const [replies, setReplies] = useState([]);

  useEffect(() => {
    const repliesQuery = query(
      collection(db, 'threads', threadId, 'replies'),
      where('isHidden', '==', false),
      orderBy('createdAt', 'asc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(
      repliesQuery,
      (snapshot) => {
        setReplies(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        if (error?.code !== 'permission-denied') {
          console.error('[BoardReplyList]', error);
        }
      }
    );

    return () => unsubscribe();
  }, [threadId]);

  if (replies.length === 0) {
    return (
      <div
        className="revisit-panel"
        style={{ borderRadius: '16px', padding: '24px', textAlign: 'center' }}
      >
        <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ui-accent-text)' }}>
          まだ返信はありません。最初のひと声で会話を動かせます。
        </p>
        <p style={{ fontSize: '12px', color: 'var(--ui-accent-muted)', marginTop: '8px' }}>
          迷ったら「どの山を想定していますか」など、答えやすいひと言から始めると返しやすくなります。
        </p>
        <div style={{ marginTop: '12px' }}>
          <a href="#reply-form" className="cta-link" style={{ padding: '8px 20px', fontSize: '12px', fontWeight: 600 }}>
            最初の1人になる
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      {replies.map((reply, index) => {
        const postNum = startIndex + index;
        const isAlt = index % 2 === 1;

        return (
          <div
            key={reply.id}
            id={`res${postNum}`}
            style={{
              backgroundColor: isAlt ? 'var(--ch-bg-post-alt)' : 'var(--ch-bg-post)',
              borderBottom: '1px solid var(--ch-border)',
              padding: '4px 8px',
            }}
          >
            <div
              style={{
                marginBottom: '4px',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: '4px 8px',
                fontSize: '12px',
              }}
            >
              <span style={{ color: 'var(--ch-post-num)', fontWeight: 'bold' }}>{postNum}</span>
              <span style={{ color: 'var(--ch-name)', fontWeight: 'bold' }}>
                {reply.authorName}
                {reply.authorTrip && (
                  <span style={{ color: 'var(--ch-trip)', fontWeight: 'normal' }}>
                    ◆{reply.authorTrip}
                  </span>
                )}
              </span>
              <span style={{ color: 'var(--ch-muted)' }}>
                {reply.createdAt ? formatDateCh(reply.createdAt.seconds) : ''}
              </span>
            </div>

            <div
              style={{
                color: 'var(--ch-text)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                lineHeight: '1.6',
              }}
            >
              {processContent(reply.content, replies, startIndex)}
            </div>

            {reply.imageUrls && reply.imageUrls.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {reply.imageUrls.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`返信画像 ${i + 1}`}
                    style={{
                      width: '96px',
                      height: '96px',
                      objectFit: 'cover',
                      border: '1px solid var(--ch-border)',
                    }}
                  />
                ))}
              </div>
            )}

            <div style={{ marginTop: '4px', display: 'flex', justifyContent: 'flex-end' }}>
              <BoardLikeButton
                type="reply"
                threadId={threadId}
                targetId={reply.id}
                initialLikesCount={reply.likesCount ?? 0}
              />
            </div>
          </div>
        );
      })}
    </>
  );
}

export default BoardReplyList;
